import { Box, Card, CardContent, Chip, Stack, Typography, Alert } from '@mui/material'
import { CloudQueue } from '@mui/icons-material'

/**
 * Where the agents in this scan actually run.
 *
 * The runtime-platform axis exists because "what built this agent" and "where
 * does it run" are different questions that were being stored as one answer.
 * A CrewAI agent on Bedrock AgentCore is CrewAI on AgentCore, not one or the
 * other. This card is the surface where that separation becomes visible, and
 * it is also the only place the product tells the user how much of the
 * platform it could actually see.
 *
 * That last part is the reason the card exists at all. Platform resources are
 * routinely configured out of band — a console, or Terraform in an
 * infrastructure repository nobody scanned — and when a platform brokers tool
 * authorization, an agent's effective permissions are not what its source
 * shows. Findings that depend on that inference are already scoped server-side
 * (services/analysis/platform_qualification.py). Saying so here means a reader
 * meets the caveat while looking at the deployment rather than discovering it
 * attached to one finding later.
 */

interface Agent {
  agent_name?: string
  code_identifier?: string
  runtime_platform?: string | null
  runtime_platform_confidence?: number | null
  platform_config_visibility?: 'in_repo' | 'partial' | 'not_visible' | null
  brokers_tool_auth?: boolean
  platform_services?: string[]
}

const PLATFORM_LABELS: Record<string, string> = {
  bedrock_agentcore: 'AWS Bedrock AgentCore',
  vertex_agent_engine: 'Vertex AI Agent Engine',
  azure_ai_foundry: 'Azure AI Foundry',
  cloudflare_workers: 'Cloudflare Workers',
}

const PLATFORM_COLORS: Record<string, string> = {
  bedrock_agentcore: '#ff9900',
  vertex_agent_engine: '#4285f4',
  azure_ai_foundry: '#0078d4',
  cloudflare_workers: '#f6821f',
}

const label = (p: string) => PLATFORM_LABELS[p] || p.replace(/_/g, ' ')

/** What the repository showed us about how the platform is configured. */

const DeploymentCard = ({ agents }: { agents: Agent[] }) => {
  const hosted = agents.filter(a => a.runtime_platform)
  // Self-hosted is a real answer, and for many teams the preferable one. A
  // card that only counted managed platforms would imply the rest were
  // undetected.
  const selfHosted = agents.length - hosted.length

  if (!agents.length) return null

  const byPlatform = new Map<string, Agent[]>()
  hosted.forEach(a => {
    const key = a.runtime_platform as string
    byPlatform.set(key, [...(byPlatform.get(key) || []), a])
  })

  // The agents whose permission findings are scoped rather than stated
  // outright: the platform brokers tool authorization and we cannot see how.
  const scoped = hosted.filter(
    a => a.brokers_tool_auth && a.platform_config_visibility !== 'in_repo',
  )

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <CloudQueue sx={{ mr: 1, verticalAlign: 'middle' }} />
          Deployment
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Where these agents are configured to run in production, as detected in
          this repository.
        </Typography>

        {!hosted.length ? (
          <Typography variant="body2">
            All {agents.length} agent{agents.length === 1 ? '' : 's'} run on infrastructure you
            manage directly. No managed agent-hosting platform is referenced in this repository.
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {[...byPlatform.entries()]
                .sort((a, b) => b[1].length - a[1].length)
                .map(([platform, list]) => {
                  return (
                    <Chip
                      key={platform}
                      label={`${label(platform)} — ${list.length} agent${list.length === 1 ? '' : 's'}`}
                      sx={{
                        borderColor: PLATFORM_COLORS[platform] || 'divider',
                        borderWidth: 2, borderStyle: 'solid', bgcolor: 'transparent',
                      }}
                    />
                  )
                })}
              {selfHosted > 0 && (
                <Chip
                  label={`Self-hosted — ${selfHosted} agent${selfHosted === 1 ? '' : 's'}`}
                  variant="outlined"
                />
              )}
            </Box>

            {scoped.length > 0 && (
              <Alert severity="info" variant="outlined">
                {scoped.length} agent{scoped.length === 1 ? ' runs' : 's run'} on a platform that
                grants tool permissions outside this repository. Permission findings for
                {scoped.length === 1 ? ' it' : ' them'} cover the tools declared here; check your
                platform console for any granted there.
              </Alert>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}

export default DeploymentCard
